import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon';
import { EmptyState } from './EmptyState';
import { DENSITY } from '../lib/levels';
import { denominatorOf, num, type CountMessage } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  rowsWord: {
    id: 'common.ui.dataTable.rowsWord',
    defaultMessage: '{count, plural, one {строка} few {строки} many {строк} other {строки}}',
    description: 'Слово-знаменатель таблицы по умолчанию («12 строк»), без числа; {count} — число строк',
  },
  emptyTitle: {
    id: 'common.ui.dataTable.emptyTitle',
    defaultMessage: 'Строк нет',
    description: 'Заголовок пустого состояния таблицы по умолчанию',
  },
  emptyReason: {
    id: 'common.ui.dataTable.emptyReason',
    defaultMessage: 'по текущему срезу подходящих записей не нашлось',
    description: 'Пояснение пустой таблицы по умолчанию (строчная фраза, после заголовка «Строк нет»)',
  },
  shownOf: {
    id: 'common.ui.dataTable.shownOf',
    defaultMessage: 'Показано {shown} из {total} {unit}',
    description: 'Счётчик под таблицей, когда строки скрыты; {shown}/{total} — числа, {unit} — слово («контуров»)',
  },
  shownOfCapped: {
    id: 'common.ui.dataTable.shownOfCapped',
    defaultMessage: 'Показано {shown} из {total} {unit}; ещё {hidden} не выведены — таблица не рассчитана на весь реестр',
    description: 'Счётчик под развёрнутой таблицей, упёршейся в потолок строк; {hidden} — число невыведенных строк',
  },
  total: {
    id: 'common.ui.dataTable.total',
    defaultMessage: '{total} {unit}',
    description: 'Счётчик под таблицей, когда выведено всё: «12 контуров»',
  },
  collapse: {
    id: 'common.ui.dataTable.collapse',
    defaultMessage: 'Свернуть',
    description: 'Кнопка под таблицей: свернуть развёрнутый список; коротко',
  },
  showFirst: {
    id: 'common.ui.dataTable.showFirst',
    defaultMessage: 'Показать первые {cap} из {total}',
    description: 'Кнопка под таблицей: развернуть только первые строки (потолок); {cap}/{total} — числа',
  },
  showAll: {
    id: 'common.ui.dataTable.showAll',
    defaultMessage: 'Показать все ({total})',
    description: 'Кнопка под таблицей: развернуть все строки; {total} — число строк',
  },
});

export interface Column<T> {
  key: string;
  header: string;
  /** Единица в заголовке колонки: «га», «м³». */
  unit?: string;
  /** Числовая колонка выравнивается вправо и получает tabular-nums. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getKey: (row: T) => string;
  /** До скольких строк показывать без раскрытия. По умолчанию — бюджет 8. */
  limit?: number;
  /** Имя знаменателя для видимого счёта: «контуров», «работ». */
  denominator?: string | CountMessage;
  empty?: { title: string; reason: string };
  /** Переход в реестр вместо разворота на месте. */
  more?: { to: string; label: string };
  /**
   * Потолок разворота: реестр района — это 10 000+ строк, и honest DOM на
   * них кладёт вкладку. Остаток честно называется числом.
   */
  hardCap?: number;
}

/**
 * Строки таблицы не интерактивны: спуск на уровень — по имени единицы внутри
 * ячейки. Сверх бюджета строк — «показать все» или переход в реестр.
 */
export function DataTable<T>({
  columns,
  rows,
  getKey,
  limit = DENSITY.tableRows,
  denominator = m.rowsWord,
  empty,
  more,
  hardCap = 500,
}: DataTableProps<T>) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  if (rows.length === 0) {
    return (
      <EmptyState
        compact
        title={empty?.title ?? t(m.emptyTitle)}
        reason={empty?.reason ?? t(m.emptyReason)}
      />
    );
  }

  const visible = expanded ? rows.slice(0, hardCap) : rows.slice(0, limit);
  const hidden = rows.length - visible.length;

  return (
    <div className="table-wrap">
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={c.numeric ? 'is-num' : undefined}
                  style={c.width ? { width: c.width } : undefined}
                  scope="col"
                >
                  {c.header}
                  {c.unit ? <span className="table__unit">, {c.unit}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={getKey(row)}>
                {columns.map((c) => (
                  // `data-col` / `data-label` — крючки для карточного вида на узком экране (см. «Размещение»):
                  // строка раскладывается по колонкам, а подпись берётся из заголовка.
                  <td key={c.key} className={c.numeric ? 'is-num' : undefined} data-col={c.key} data-label={c.unit ? `${c.header}, ${c.unit}` : c.header}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table__foot">
        {/* «Показано 3 из 3» — счётчик там, где показывать нечего: доля равна
            единице и ничего не сообщает. Когда выведено всё, остаётся только
            величина выборки — знаменатель таблицы никуда не девается. */}
        <span>
          {hidden > 0 ? (
            <>
              {expanded
                ? t(m.shownOfCapped, { shown: num(visible.length), total: num(rows.length), unit: denominatorOf(rows.length, denominator), hidden: num(hidden) })
                : t(m.shownOf, { shown: num(visible.length), total: num(rows.length), unit: denominatorOf(rows.length, denominator) })}
            </>
          ) : (
            <>
              {t(m.total, { total: num(rows.length), unit: denominatorOf(rows.length, denominator) })}
            </>
          )}
        </span>
        <span style={{ display: 'flex', gap: 'var(--sp-4)' }}>
          {hidden > 0 || expanded ? (
            <button type="button" className="linkish focus-ring" onClick={() => setExpanded((v) => !v)}>
              <Icon icon={expanded ? ChevronUp : ChevronDown} size={16} />
              {/* Потолок разворота честен в самой кнопке: обещать «все» и
                  показать 500 из 10 812 — это обещание, которого не будет. */}
              {expanded
                ? t(m.collapse)
                : rows.length > hardCap
                  ? t(m.showFirst, { cap: num(hardCap), total: num(rows.length) })
                  : t(m.showAll, { total: num(rows.length) })}
            </button>
          ) : null}
          {more ? (
            <Link className="linkish" to={more.to}>
              {more.label}
            </Link>
          ) : null}
        </span>
      </div>
    </div>
  );
}
